import React, { useEffect, useState } from 'react';

import Icon from 'views/site/Icon.js';
import LeadQuizSheet from 'views/site/LeadQuizSheet.js';
import LeadQuizView from 'views/site/LeadQuiz.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * LeadQuizModal
 *
 * Modal host for the ad walkthrough, over the home page rather than on a page
 * of its own. Somebody arriving from an ad has not chosen to visit the site,
 * and a bare form on a bare url gives them nothing to decide with - opening
 * over the home page means the dealership's name, phone number and photographs
 * are behind it while they answer.
 *
 * Mounted only while open, so closing and reopening starts a fresh walkthrough.
 * That is the opposite of the coverage quiz, which is resumed deliberately -
 * this one is short, and half an old set of answers attached to a lead is worse
 * than none.
 */
const LeadQuizModal = ({ dealership, onClose, open, utils }) => {

    const mobile = Utils.isMobile();

    /*
     * Mounted a beat longer than it is open.
     *
     * Returning null the moment `open` goes false leaves nothing on screen to
     * animate, so the modal vanished rather than left. `mounted` keeps it in the
     * tree for the length of the exit, and `active` is the flag the css
     * transitions read — it goes true on the frame after mount so the browser
     * has a starting style to move away from.
     */
    const [active, setActive] = useState(false);
    const [mounted, setMounted] = useState(open);

    useEffect(() => {
        if(open === true) {
            setMounted(true);
            return;
        }
        setActive(false);
        let timer = window.setTimeout(() => setMounted(false), 240);
        return () => window.clearTimeout(timer);
    }, [open]);

    /*
     * Two frames, not one.
     *
     * Mounting the element and flipping the flag that drives the transition
     * cannot happen in the same frame: the browser has to paint the starting
     * style before it has anything to animate away from, and a single
     * requestAnimationFrame can still run inside the commit that first put the
     * element in the DOM. The first frame guarantees the paint, the second
     * flips the flag. This is why the enter was snapping while the exit — which
     * starts from an element that has been on screen for a while — was fine.
     */
    useEffect(() => {
        if(mounted === false || open === false) {
            return;
        }
        let inner = null;
        let outer = window.requestAnimationFrame(() => {
            inner = window.requestAnimationFrame(() => setActive(true));
        });
        return () => {
            window.cancelAnimationFrame(outer);
            if(inner !== null) {
                window.cancelAnimationFrame(inner);
            }
        };
    }, [mounted, open]);

    useEffect(() => {
        if(!open) {
            return;
        }
        const onKeyDown = evt => evt.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    // the page behind must not scroll while this is up
    useEffect(() => {
        document.body.style.overflowY = open ? 'hidden' : 'scroll';
        return () => { document.body.style.overflowY = 'scroll'; };
    }, [open]);

    if(mounted === false) {
        return null;
    }

    /*
     * A phone gets a sheet it can throw away; a desktop gets the centred card.
     * The scrim is shared - both are modal tasks, and a modal task dims what is
     * behind it so there is one place to look.
     */
    if(mobile === true) {
        return (
            <>
                <div
                className={'site-overlay quiz-modal-scrim'}
                data-active={active}
                onClick={onClose} />

                <LeadQuizSheet onClose={onClose} open={active}>
                    {scroller => <LeadQuizView dealership={dealership} onClose={onClose} scrollRef={scroller} utils={utils} />}
                </LeadQuizSheet>
            </>
        )
    }

    return (
        <>
            <div
            className={'site-overlay quiz-modal-scrim'}
            data-active={active}
            onClick={onClose} />

            <div
            className={'site'}
            style={{
                alignItems: 'center',
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                left: 0,
                padding: mobile ? 12 : 20,
                position: 'fixed',
                right: 0,
                top: 0,
                zIndex: 6500
            }}>
                <div
                className={'quiz-modal-surface'}
                data-active={active}
                onClick={evt => evt.stopPropagation()}
                style={{
                    backgroundColor: Theme.colors.white,
                    borderRadius: 24,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                    display: 'flex',
                    flexDirection: 'column',

                    // the scrim now covers the header, so this is free to use the
                    // window rather than stopping politely underneath the nav
                    maxHeight: 'calc(100vh - 48px)',
                    maxWidth: 720,

                    // the view scrolls its own body; the surface must not scroll
                    // too or the pinned chrome scrolls away with it
                    overflow: 'hidden',
                    position: 'relative',
                    width: '100%'
                }}>
                    <div
                    className={'pressable'}
                    onClick={onClose}
                    style={{
                        alignItems: 'center',
                        backgroundColor: Theme.colors.offWhite,
                        borderRadius: 18,
                        cursor: 'pointer',
                        display: 'flex',
                        height: 36,
                        justifyContent: 'center',
                        boxShadow: '0 0 0 6px rgba(255,255,255,0.9)',
                        position: 'absolute',
                        right: 20,
                        top: 20,
                        width: 36,
                        zIndex: 2
                    }}>
                        <Icon name={'x-lg'} size={14} />
                    </div>

                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        padding: mobile ? '36px 22px 22px' : '44px 44px 28px'
                    }}>
                        <LeadQuizView dealership={dealership} onClose={onClose} utils={utils} />
                    </div>
                </div>
            </div>
        </>
    )
}

export default LeadQuizModal;
